<script setup lang="ts">
    import { computed, watch, onMounted, ref } from 'vue'
    import { useRoute } from 'vue-router'
    import { SavingState } from '@common/Common'
    import CorporateNavigation from "@common/Components/CorporateNavigation.vue"
    import SaveButton from '@common/Components/SaveButton.vue'
    import { useCorporatePnlTemplateByIdQueryV1 } from '@Quest/Pnls/queries-vue-composables'
    import { useUpdatePnlCommandV1 } from '@Quest/Pnls/commands-vue-composables'
    import type { CorporatePnlTemplateReadModel } from '@Quest/Pnls/readmodels'
    import type { ValidationError } from '@common/types'

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params.strategyId ?? ''))
    const pnlId = computed(() => String(route.params.pnlId ?? ''))

    const initialized = ref<boolean>(false)
    const name = ref<string>('')
    const startYear = ref<number | null>(null)
    const startMonth = ref<number | null>(null)
    const columns = ref<string | null>(null)

    const savingState = ref<SavingState>(SavingState.Pending)
    const errors = ref<ValidationError[]>([])
    const nameError = computed(() => errors.value.find(e => e.ErrorCode.startsWith("Name."))?.Error ?? null)
    const columnsError = computed(() => errors.value.find(e => e.ErrorCode.startsWith("Columns."))?.Error ?? null)


    const {
        runCorporatePnlTemplateByIdQueryV1,
        modelCorporatePnlTemplateByIdQueryV1
    } = useCorporatePnlTemplateByIdQueryV1(workspaceId, strategyId, pnlId)

    const {
        invokeUpdatePnlCommandV1,
        onSucceedUpdatePnlCommandV1,
        validationErrorsUpdatePnlCommandV1
    } = useUpdatePnlCommandV1(workspaceId, strategyId, pnlId, name, startYear, startMonth, columns)

    function handleUpdatePnlCommand()
    {
        errors.value = [];
        savingState.value = SavingState.InProgress
        invokeUpdatePnlCommandV1()
    }


    onMounted(() =>
    {
        document.title = "Edit P&L | TransparentChoice"
        runCorporatePnlTemplateByIdQueryV1()
    })

    watch(modelCorporatePnlTemplateByIdQueryV1, (rm: CorporatePnlTemplateReadModel | null) =>
    {
        if (rm)
        {
            name.value = rm.Name
            startYear.value = rm.StartYear
            startMonth.value = rm.StartMonth
            columns.value = rm.Columns
            initialized.value = true
        }
    })

    onSucceedUpdatePnlCommandV1(() =>
    {
        savingState.value = SavingState.Pending
        alert('Saved')
    })

    watch(validationErrorsUpdatePnlCommandV1, (validationErrors) =>
    {
        if (!validationErrors || validationErrors.length === 0) return
        errors.value = validationErrors
        savingState.value = SavingState.Pending
    })


</script>

<template>
    <div>
        <CorporateNavigation ActivePage="strategy" />

        <div v-if="initialized" class="container" style="margin-top: 80px; padding-top: 40px;">
            <form @submit.prevent="handleUpdatePnlCommand" data-testid="edit-pnl-form">
                <div class="row">
                    <div class="col-lg-6">
                        <h6>
                            <a class="text-dark" v-sanitizedHref="`/#/enterprise/pnls/${workspaceId}/${strategyId}`" data-testid="back-to-pnls-link">
                                <i class="fa fa-angle-double-left" aria-hidden="true"></i> Back to P&Ls
                            </a>
                        </h6>
                        <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;" data-testid="page-heading">Edit P&L template</h1>
                    </div>
                    <div class="col-lg-6 text-right align-self-end">
                        <SaveButton :savingState="savingState" :isFormSubmit="true" data-testid="save-button"></SaveButton>
                    </div>
                </div>

                <hr style="border-color: #bbb; margin: 20px 0 60px;" />

                <div class="form-group">
                    <label>Name:</label>
                    <input type="text" v-model.trim="name" class="form-control" required data-testid="name-input" />
                    <div class="error" data-testid="name-error">{{nameError}}</div>
                </div>

                <div class="form-group">
                    <label>Start year:</label>
                    <input type="number" v-model.number="startYear" class="form-control" min="2023" max="2100" step="1" data-testid="start-year-input"/>
                </div>

                <div class="form-group">
                    <label>Start month:</label>
                    <select class="form-control" v-model="startMonth" data-testid="start-month-select">
                        <option :value="null">Current month (dynamic)</option>
                        <option v-for="i in 12" :key="i" :value="i">{{i}}</option>
                    </select>
                </div>
                <div class="form-group">
                    <label>Columns:</label>
                    <textarea v-model.trim="columns" class="form-control" required data-testid="columns-input" />
                    <div class="error" data-testid="columns-error">{{columnsError}}</div>
                </div>
            </form>
        </div>
    </div>
</template>